import { lazy, Suspense, useEffect } from 'react';
import { LoadScript } from '@react-google-maps/api';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Index from "./jsx";
import { connect, useDispatch } from 'react-redux';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { checkAutoLogin } from './services/AuthService';
import { isAuthenticated } from './store/selectors/AuthSelectors';
import 'bootstrap-select/dist/css/bootstrap-select.min.css';
import "./css/style.css";

/* Retry wrapper for lazy imports */
const retryLazy = (fn, retriesLeft = 3, interval = 1000) =>
    new Promise((resolve, reject) => {
        fn().then(resolve).catch(error => {
            setTimeout(() => {
                if (retriesLeft === 1) {
                    window.location.reload(); // final fallback
                } else {
                    retryLazy(fn, retriesLeft - 1, interval).then(resolve, reject);
                }
            }, interval);
        });
    });

/* Lazy loaded pages with retry */
const SignUp = lazy(() => retryLazy(() => import('./jsx/pages/Registration')));
const ForgotPassword = lazy(() => retryLazy(() => import('./jsx/pages/ForgotPassword')));
const CityLogin = lazy(() => retryLazy(() => import('./jsx/pages/CityLogin'))); // ✅ keep same page name
const ShopLogin = lazy(() => retryLazy(() => import('./jsx/pages/ShopVendorLogin')));
const CustomerPrivacy = lazy(() => retryLazy(() => import('./jsx/pages/CustomerPrivacy')));
const VendorPrivacy = lazy(() => retryLazy(() => import('./jsx/pages/VendorPrivacy')));
const DeliveryPrivacy = lazy(() => retryLazy(() => import('./jsx/pages/DeliveryPrivacy')));
const POS = lazy(() => retryLazy(() => import('./jsx/pages/POS')));

const Login = lazy(() => retryLazy(() =>
    new Promise(resolve => {
        setTimeout(() => resolve(import('./jsx/pages/Login')), 500);
    })
));

const libraries = ['drawing'];

/* Router HOC */
function withRouter(Component) {
    function ComponentWithRouterProp(props) {
        let location = useLocation();
        let navigate = useNavigate();
        let params = useParams();
        return <Component {...props} router={{ location, navigate, params }} />;
    }
    return ComponentWithRouterProp;
}

function App(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        // Auto-login check
        checkAutoLogin(dispatch, navigate);

        // Redirect from "/" to "/warehouse/login"
        if (window.location.pathname === "/") {
            navigate("/warehouse/login", { replace: true }); // ✅ changed path only
        }

        // Global listener for chunk load failures
        const handleChunkError = (e) => {
            if (e?.message?.includes('Loading chunk') || e?.message?.includes('ChunkLoadError')) {
                console.warn("Chunk load failed, reloading...");
                window.location.reload();
            }
        };

        window.addEventListener('error', handleChunkError);
        return () => {
            window.removeEventListener('error', handleChunkError);
        };
    }, [dispatch, navigate]);

    const routeblog = (
        <Routes>
            <Route path="/" element={<CityLogin />} /> {/* ✅ same page */}
            <Route path='/veeadmin/login' element={<Login />} />
            <Route path='/warehouse/login' element={<CityLogin />} /> {/* ✅ changed path */}
            <Route path='/shopvendor/login' element={<ShopLogin />} />
            <Route path='/page-register' element={<SignUp />} />
            <Route path='/page-forgot-password' element={<ForgotPassword />} />
            <Route path='/customer-privacy' element={<CustomerPrivacy />} />
            <Route path='/vendor-privacy' element={<VendorPrivacy />} />
            <Route path='/delivery-privacy' element={<DeliveryPrivacy />} />
             <Route path='/pos' element={<POS />} />
        </Routes>
    );

    return (
        <>
            <LoadScript
                googleMapsApiKey="AIzaSyBl7sha9y9z62uGGwsUFB17TnsGRad7TQ8"
                libraries={libraries}
                onLoad={() => {
                    console.log('Google Maps script loaded');
                }}
            >
                <Suspense fallback={
                    <div id="preloader">
                        <div className="sk-three-bounce">
                            <div className="sk-child sk-bounce1"></div>
                            <div className="sk-child sk-bounce2"></div>
                            <div className="sk-child sk-bounce3"></div>
                        </div>
                    </div>
                }>
                    {props.isAuthenticated
                        ? <Index />
                        : <div className="vh-100">{routeblog}</div>}
                </Suspense>
            </LoadScript>

            {/* Toasts */}
            <ToastContainer position="top-right" autoClose={3000} />
        </>
    );
}

const mapStateToProps = (state) => ({
    isAuthenticated: isAuthenticated(state),
});

export default withRouter(connect(mapStateToProps)(App));
